import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { ViewOnlyBadge } from '@/components/templates/app-shell'
import { displayShortDate } from '@/lib/dates'

interface FinancialHistoryItem {
  event: string
  contact: string
  amount: string
  amount_cents: number
  occurred_on: string
  href: string
  money_out: boolean
}

interface FinancialHistoryResponse {
  financial_history: FinancialHistoryItem[]
}

export function FinancialHistoryPage() {
  const [search, setSearch] = useState('')
  const [type, setType] = useState('All')
  const [sort, setSort] = useState<Sort>({ column: 'date', direction: 'descending' })
  const [page, setPage] = useState(0)
  const historyQuery = useQuery({
    queryKey: ['financial-history'],
    queryFn: () => getJSON<FinancialHistoryResponse>('/api/v1/financial_history'),
    retry: false,
  })
  const entries = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    const filtered = [...(historyQuery.data?.financial_history ?? [])]
      .filter((item) => type === 'All' || entryType(item.event) === type)
      .filter(
        (item) =>
          !normalizedSearch ||
          `${item.event} ${item.contact}`.toLowerCase().includes(normalizedSearch),
      )
    return filtered.sort((left, right) => sortEntries(left, right, sort))
  }, [historyQuery.data?.financial_history, search, sort, type])
  const pageCount = Math.ceil(entries.length / PAGE_SIZE)
  const visibleEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  if (historyQuery.isLoading)
    return <EmptyState title="Loading financial history" className="min-h-52" />
  if (historyQuery.isError || !historyQuery.data)
    return <EmptyState title="Financial history is unavailable" className="min-h-52" />

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Financial history</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          Every payment, invoice, refund, payout, and chargeback across the association.
        </p>
      </header>
      <HistoryFilters
        search={search}
        setSearch={(nextSearch) => {
          setSearch(nextSearch)
          setPage(0)
        }}
        type={type}
        setType={(nextType) => {
          setType(nextType)
          setPage(0)
        }}
      />
      {entries.length === 0 ? (
        <EmptyState className="mt-4" variant="quiet" title="No activity yet." />
      ) : (
        <>
          <p className="mt-4 mb-2 text-caption text-muted-foreground">{entries.length} entries</p>
          <HistoryTable
            entries={visibleEntries}
            sort={sort}
            setSort={(nextSort) => {
              setSort(nextSort)
              setPage(0)
            }}
          />
          <HistoryPager page={page} pageCount={pageCount} setPage={setPage} />
        </>
      )}
    </div>
  )
}

function HistoryFilters({
  search,
  setSearch,
  type,
  setType,
}: {
  search: string
  setSearch: (value: string) => void
  type: string
  setType: (value: string) => void
}) {
  return (
    <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="financial-history-type">Type</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger id="financial-history-type" className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['All', 'Payments', 'Invoices', 'Refunds', 'Payouts', 'Chargebacks'].map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="financial-history-search">Search</Label>
        <Input
          id="financial-history-search"
          aria-label="Search contact or event"
          className="h-9 w-56"
          placeholder="Search contact or event"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
    </div>
  )
}

function HistoryTable({
  entries,
  sort,
  setSort,
}: {
  entries: FinancialHistoryItem[]
  sort: Sort
  setSort: (sort: Sort) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead
            active={sort.column === 'date'}
            direction={sort.column === 'date' ? sort.direction : 'descending'}
            onToggle={() => setSort(nextSort(sort, 'date'))}
          >
            Date
          </SortableTableHead>
          <TableHead>Event</TableHead>
          <TableHead>Contact</TableHead>
          <SortableTableHead
            className="text-right"
            active={sort.column === 'amount'}
            direction={sort.column === 'amount' ? sort.direction : 'descending'}
            onToggle={() => setSort(nextSort(sort, 'amount'))}
          >
            Amount
          </SortableTableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((item) => (
          <TableRow
            key={`${item.event}-${item.occurred_on}-${item.href}`}
            className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            role="link"
            tabIndex={0}
            onClick={() => window.location.assign(item.href)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') window.location.assign(item.href)
            }}
          >
            <TableCell className="text-caption text-muted-foreground">
              {shortDate(item.occurred_on)}
            </TableCell>
            <TableCell className="text-body-sm">{item.event}</TableCell>
            <TableCell
              className={
                item.contact === '—' ? 'text-body-sm text-muted-foreground' : 'text-body-sm'
              }
            >
              {item.contact}
            </TableCell>
            <TableCell
              className={`text-right text-body-sm tabular-nums ${item.money_out ? 'text-destructive' : ''}`}
            >
              {item.amount}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function entryType(event: string) {
  if (event.startsWith('Payment')) return 'Payments'
  if (event.startsWith('Invoice')) return 'Invoices'
  if (event.startsWith('Refund')) return 'Refunds'
  if (event.startsWith('Payout')) return 'Payouts'
  if (event.startsWith('Chargeback')) return 'Chargebacks'
  return null
}

const PAGE_SIZE = 25

type Sort = { column: 'date' | 'amount'; direction: 'ascending' | 'descending' }

function nextSort(current: Sort, column: Sort['column']): Sort {
  if (current.column !== column) return { column, direction: 'ascending' }
  return { column, direction: current.direction === 'descending' ? 'ascending' : 'descending' }
}

function sortEntries(left: FinancialHistoryItem, right: FinancialHistoryItem, sort: Sort) {
  const comparison =
    sort.column === 'date'
      ? left.occurred_on.localeCompare(right.occurred_on)
      : left.amount_cents - right.amount_cents
  return sort.direction === 'descending' ? -comparison : comparison
}

function HistoryPager({
  page,
  pageCount,
  setPage,
}: {
  page: number
  pageCount: number
  setPage: (page: number) => void
}) {
  return (
    <div className="mt-3 flex justify-end gap-2">
      <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>
        ‹ Previous
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={page >= pageCount - 1}
        onClick={() => setPage(page + 1)}
      >
        Next ›
      </Button>
    </div>
  )
}

const shortDate = displayShortDate
